/**
 * Partner SSE: подписывается на /api/v2/affiliate/stream и инвалидирует
 * соответствующие React Query ключи при получении server-published событий.
 *
 * Также фанаутит события на window (см. live-events.ts) — так конверсии
 * могут запускать confetti / tab-heartbeat / звук / browser-notification
 * без прямого coupling.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { emitConversion, emitPayout, emitSseState } from './live-events';

interface ConversionEventPayload {
  id?: string;
  conversionId?: string;
  commissionCents?: number;
  amountCents?: number;
  offerSlug?: string;
  country?: string;
  createdAt?: string;
}

interface PayoutEventPayload {
  id?: string;
  payoutId?: string;
  amountCents?: number;
  createdAt?: string;
}

function tryParse<T>(raw: string | null | undefined): T | null {
  if (!raw) return null;
  try { return JSON.parse(raw) as T; } catch { return null; }
}

export function usePartnerLiveUpdates(enabled: boolean = true): void {
  const qc = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') return;

    const es = new EventSource('/api/v2/affiliate/stream', { withCredentials: true });

    const onStatus = (): void => {
      qc.invalidateQueries({ queryKey: ['affiliate', 'me'] });
    };
    const onTier = (): void => {
      qc.invalidateQueries({ queryKey: ['affiliate', 'me'] });
    };
    const onConversion = (e: MessageEvent): void => {
      qc.invalidateQueries({ queryKey: ['affiliate', 'conversions'] });
      qc.invalidateQueries({ queryKey: ['affiliate', 'balance'] });
      qc.invalidateQueries({ queryKey: ['affiliate', 'ledger'] });
      qc.invalidateQueries({ queryKey: ['affiliate', 'kpis'] });
      qc.invalidateQueries({ queryKey: ['affiliate', 'series'] });
      const p = tryParse<ConversionEventPayload>(e.data);
      emitConversion({
        id: p?.id ?? p?.conversionId ?? `conv-${Date.now()}`,
        amountCents: p?.commissionCents ?? p?.amountCents ?? 0,
        offerLabel: p?.offerSlug,
        countryCode: p?.country ?? undefined,
        iso: p?.createdAt ?? new Date().toISOString(),
      });
    };
    const onPayout = (e: MessageEvent): void => {
      qc.invalidateQueries({ queryKey: ['affiliate', 'payouts'] });
      qc.invalidateQueries({ queryKey: ['affiliate', 'balance'] });
      qc.invalidateQueries({ queryKey: ['affiliate', 'ledger'] });
      const p = tryParse<PayoutEventPayload>(e.data);
      emitPayout({
        id: p?.id ?? p?.payoutId ?? `payout-${Date.now()}`,
        amountCents: p?.amountCents ?? 0,
        iso: p?.createdAt ?? new Date().toISOString(),
      });
    };
    const onOfferStatus = (): void => {
      qc.invalidateQueries({ queryKey: ['affiliate', 'offers'] });
    };
    const onMessage = (): void => {
      qc.invalidateQueries();
    };
    const onOpen = (): void => emitSseState(true);

    es.addEventListener('open', onOpen);
    es.addEventListener('message', onMessage);
    es.addEventListener('affiliate:status_changed', onStatus);
    es.addEventListener('affiliate:tier_changed', onTier);
    es.addEventListener('affiliate:conversion', onConversion as EventListener);
    es.addEventListener('affiliate:payout', onPayout as EventListener);
    es.addEventListener('offer:status', onOfferStatus);

    es.onerror = (): void => {
      emitSseState(false);
      // EventSource auto-reconnects; this only signals current state.
      // eslint-disable-next-line no-console
      console.debug('[partner-sse] connection error, will retry');
    };

    return () => {
      es.removeEventListener('open', onOpen);
      es.removeEventListener('message', onMessage);
      es.removeEventListener('affiliate:status_changed', onStatus);
      es.removeEventListener('affiliate:tier_changed', onTier);
      es.removeEventListener('affiliate:conversion', onConversion as EventListener);
      es.removeEventListener('affiliate:payout', onPayout as EventListener);
      es.removeEventListener('offer:status', onOfferStatus);
      es.close();
      emitSseState(false);
    };
  }, [qc, enabled]);
}
